import type { FC } from 'react';
import { useLayoutEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import { Button } from 'src/components/buttons-and-actions/button';
import { DropdownTrigger } from 'src/components/buttons-and-actions/dropdown-trigger';
import { ChevronLeftIcon } from 'src/components/media-and-icons/icons/chevron-left';
import { ChevronRightIcon } from 'src/components/media-and-icons/icons/chevron-right';
import { Link } from 'src/components/navigation/link';
import { useSearchParams } from 'src/hooks/use-search-params';
import { cn } from 'src/utils/cn';

export type PaginationProps = (
  | {
      start: number;
      totalSize: number;
      perPage: number;
      offset?: undefined;
      total?: undefined;
      limit?: undefined;
    }
  | {
      start?: undefined;
      totalSize?: undefined;
      perPage?: undefined;
      offset: number;
      total: number;
      limit: number;
    }
) & {
  paramPrefix?: string;
  configurable?: boolean;
};

const paginationLinkClassNames = cn(`
  hover:bg-background-surface-hover hover:text-foreground-secondary-hover
  focus:bg-background-surface-hover focus:text-foreground-secondary-hover
`);
const paginationItemClassNames = cn(
  `flex min-w-[26px] items-center justify-center rounded-xs py-xs`
);

/**
 * Most of the time the `<Pagination />` component is used inside our
 * `<Table />` component, but in case you need to use it _outside_ of a table,
 * you can use this dedicated component.
 */
export const Pagination: FC<PaginationProps> = (props) => {
  const { offset, total, limit, configurable, paramPrefix } =
    normalizePaginationProps(props);

  const params = useSearchParams();
  const navigate = useNavigate();

  const startKey = paramPrefix ? `${paramPrefix}start` : 'start';
  const pageSizeKey = paramPrefix ? `${paramPrefix}pageSize` : 'pageSize';

  const { end, lastPage, currentPage, pages } = useMemo(() => {
    const currentPage = Math.floor(offset / limit) + 1;
    const lastPage = Math.floor((total - 1) / limit) + 1;

    const slice = offsets
      .map((offset) => currentPage + offset)
      .filter((page) => {
        const items = page * limit;
        return items > 0 && items < total + limit;
      });

    const firstInSlice = slice[0]!;
    const lastInSlice = slice[slice.length - 1]!;

    const pages: Array<number | null> = [...slice];

    if (lastInSlice !== lastPage) {
      // removes up to two items on end and appends [null, lastPage]
      const deleteCount = clamp(currentPage - firstInSlice, 0, 2);
      pages.splice(pages.length - deleteCount, deleteCount, null, lastPage);
    }
    if (firstInSlice !== 1) {
      // removes up to two items on start and prepends [1, null]
      const deleteCount = clamp(lastInSlice - currentPage, 0, 2);
      pages.splice(0, deleteCount, 1, null);
    }

    const end = offset + limit > total ? total : offset + limit;

    return { end, lastPage, currentPage, pages };
  }, [limit, offset, total]);

  const containerRef = useRef<HTMLDivElement>(null);
  const lastElementTopOffsetRef = useRef<number | undefined>(undefined);
  const lastPageRef = useRef(currentPage);

  // Keeps the pagination control pinned to its current viewport position
  // whenever pagination happens (row count or row height can change),
  // which can shift the layout above it.
  useLayoutEffect(() => {
    const elementTopOffset = getElementTopOffset(containerRef.current);
    const previousElementTopOffset = lastElementTopOffsetRef.current;

    if (
      lastPageRef.current !== currentPage &&
      elementTopOffset !== undefined &&
      previousElementTopOffset !== undefined
    ) {
      window.scrollBy(0, elementTopOffset - previousElementTopOffset);
    }

    lastElementTopOffsetRef.current = elementTopOffset;
    lastPageRef.current = currentPage;
  });

  const createPageLink = (start: number) => {
    const newParams = new URLSearchParams(params);
    if (start) {
      newParams.set(startKey, start.toString());
    } else {
      newParams.delete(startKey);
    }
    return `?${newParams}`;
  };

  const navigateToPage = (start: number) => {
    navigate({ search: createPageLink(start) });
  };

  const testidPrefix = paramPrefix ? `${paramPrefix}-` : '';

  if (!total) return null;

  return (
    <div className="@container/pagination">
      <div
        ref={containerRef}
        className={`
          flex flex-col-reverse items-center justify-between gap-lg py-lg
          @sm/pagination:flex-row @sm/pagination:justify-between
        `}
      >
        <div
          data-testid="pagination-info"
          className="flex items-center gap-sm text-foreground-surface-subdued"
        >
          Showing{' '}
          {configurable ? (
            <PaginationConfig
              start={offset + 1}
              end={end}
              total={total}
              pageSizeKey={pageSizeKey}
            />
          ) : (
            <div>
              {offset + 1}—{end} of {total}
            </div>
          )}{' '}
          items
        </div>

        {lastPage > 1 && (
          <div
            className="flex items-stretch gap-xs"
            data-testid="pagination-control"
          >
            <Button
              mode="link"
              aria-label="Previous page"
              disabled={currentPage === 1}
              onClick={() => navigateToPage((currentPage - 2) * limit)}
              className={cn(paginationLinkClassNames, paginationItemClassNames)}
              data-testid={`${testidPrefix}previous-page`}
            >
              <ChevronLeftIcon size="sm" />
            </Button>

            <div
              className={`
                flex items-center
                @lg/pagination:hidden
              `}
            >
              <DropdownTrigger
                mode="link"
                placement="bottom"
                label={`Page ${currentPage}`}
                data-testid={`${testidPrefix}pagination-page-dropdown-trigger`}
              >
                <PageProgression
                  pages={pages}
                  currentPage={currentPage}
                  createPageLink={createPageLink}
                  limit={limit}
                />
              </DropdownTrigger>
            </div>

            <div
              className={`
                hidden
                @lg/pagination:block
              `}
            >
              <PageProgression
                pages={pages}
                currentPage={currentPage}
                createPageLink={createPageLink}
                limit={limit}
              />
            </div>

            <Button
              mode="link"
              aria-label="Next page"
              disabled={currentPage === lastPage}
              onClick={() => navigateToPage(currentPage * limit)}
              className={cn(paginationLinkClassNames, paginationItemClassNames)}
              data-testid={`${testidPrefix}next-page`}
            >
              <ChevronRightIcon size="sm" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

type InternalPaginationProps = {
  offset: number;
  total: number;
  limit: number;
  paramPrefix: string;
  configurable: boolean;
};

const offsets = [-4, -3, -2, -1, 0, 1, 2, 3, 4];

function clamp(num: number, min: number, max: number) {
  return num <= min ? min : num >= max ? max : num;
}

/**
 * The element's offset relative to the document. Adding scrollY produces
 * a value that's not dependent on the current scrolling position.
 *
 * Please note this is just working in a window level scrolling page.
 */
function getElementTopOffset(element: HTMLElement | null) {
  if (!element) return;
  return element.getBoundingClientRect().top + window.scrollY;
}

const PaginationConfig: FC<{
  start: number;
  end: number;
  total: number;
  pageSizeKey: string;
}> = (props) => {
  const { start, end, total, pageSizeKey } = props;

  const params = useSearchParams();
  const currentPageSize = Number(params.get(pageSizeKey)) || 10;

  const createPageLink = (items: number) => {
    const newParams = new URLSearchParams(params);

    newParams.set(pageSizeKey, items.toString());

    return `?${newParams}`;
  };

  return (
    <div className="flex" data-testid="pagination-config">
      <DropdownTrigger
        mode="link"
        label={`${start}—${end}`}
        data-testid="pagination-config-dropdown-trigger"
      >
        <Link isActive={currentPageSize === 10} to={createPageLink(10)}>
          10 Items
        </Link>
        <Link isActive={currentPageSize === 25} to={createPageLink(25)}>
          25 Items
        </Link>
        <Link isActive={currentPageSize === 50} to={createPageLink(50)}>
          50 Items
        </Link>
      </DropdownTrigger>
      <span className="whitespace-pre"> of {total}</span>
    </div>
  );
};

const PageProgression: FC<{
  pages: Array<number | null>;
  currentPage: number;
  createPageLink: (start: number) => string;
  limit: number;
}> = ({ pages, currentPage, createPageLink, limit }) => (
  <div className="flex items-stretch gap-xs">
    {pages.map((page, index) =>
      page !== null ? (
        <PageLink
          key={page}
          active={page === currentPage}
          to={createPageLink((page - 1) * limit)}
        >
          {page}
        </PageLink>
      ) : (
        <div
          key={`empty-${index}`}
          className="py-xs text-foreground-surface-subdued"
        >
          …
        </div>
      )
    )}
  </div>
);

const PageLink: FC<{
  active?: boolean;
  to: string;
  children: number;
}> = ({ active, to, children }) => {
  if (active) {
    return (
      <span
        className={cn(
          `
            rounded-xs bg-background-surface-active py-xs text-center
            text-foreground-surface-active
          `,
          paginationItemClassNames
        )}
      >
        {children}
      </span>
    );
  }

  return (
    <Link
      to={to}
      mode="default"
      className={cn(paginationLinkClassNames, paginationItemClassNames)}
    >
      {children}
    </Link>
  );
};

export function normalizePaginationProps(
  pagination: PaginationProps
): InternalPaginationProps {
  const offset = pagination.offset ?? pagination.start;
  const total = pagination.totalSize ?? pagination.total;
  const limit = pagination.limit ?? pagination.perPage;
  return {
    offset,
    total,
    limit,
    configurable: pagination.configurable ?? true,
    paramPrefix: pagination.paramPrefix ?? '',
  };
}
